import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { Modal } from './Modal';
import { Button } from './Button';

const ScannerContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  background: #000;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
`;

const VideoElement = styled.video`
  width: 100%;
  height: auto;
  display: block;
`;

const ScannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const ScanArea = styled.div`
  width: 250px;
  height: 250px;
  border: 3px solid ${props => props.theme.colors.primary};
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: ${props => props.theme.colors.primary};
    border-style: solid;
  }
  
  &::before {
    top: -3px;
    left: -3px;
    border-width: 5px 0 0 5px;
  }
  
  &::after {
    bottom: -3px;
    right: -3px;
    border-width: 0 5px 5px 0;
  }
`;

const ScanLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: ${props => props.theme.colors.primary};
  animation: scan 2s linear infinite;
  
  @keyframes scan {
    0% {
      top: 0;
    }
    100% {
      top: 100%;
    }
  }
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
  align-items: center;
`;

const StatusMessage = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.isError ? 'rgba(231, 76, 60, 0.1)' : 'rgba(135, 206, 235, 0.1)'};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.isError ? props.theme.colors.error : props.theme.colors.primary};
`;

const CameraSelect = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  font-size: ${props => props.theme.typography.fontSize.base};
  width: 100%;
  max-width: 300px;
`;

const TipsList = styled.ul`
  text-align: left;
  margin-top: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  
  li {
    margin-bottom: ${props => props.theme.spacing.xs};
  }
`;

export const ZXingScanner = ({ isOpen, onClose, onScan }) => {
  const videoRef = useRef(null);
  const [reader, setReader] = useState(null);
  const [error, setError] = useState('');
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastScanned, setLastScanned] = useState('');
  const scanTimeoutRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      initializeScanner();
    }
    
    return () => {
      stopScanning();
    };
  }, [isOpen]);

  useEffect(() => {
    if (selectedDeviceId && reader && scanning) {
      startScanning(selectedDeviceId);
    }
  }, [selectedDeviceId]);

  const initializeScanner = async () => {
    try {
      const codeReader = new BrowserMultiFormatReader();
      setReader(codeReader);
      
      // Obtener lista de cámaras disponibles
      const videoDevices = await codeReader.listVideoInputDevices();
      if (videoDevices.length === 0) {
        setError('No se encontraron cámaras disponibles');
        return;
      }
      
      setDevices(videoDevices);
      
      // Preferir cámara trasera si está disponible
      const backCamera = videoDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        device.label.toLowerCase().includes('trasera') ||
        device.label.toLowerCase().includes('rear')
      );
      
      const defaultDevice = backCamera || videoDevices[0];
      setSelectedDeviceId(defaultDevice.deviceId);
      
      startScanning(defaultDevice.deviceId, codeReader);
    } catch (err) {
      console.error('Error initializing scanner:', err);
      setError('Error al inicializar el escáner: ' + err.message);
    }
  };

  const startScanning = async (deviceId, codeReaderInstance = reader) => {
    if (!codeReaderInstance || !videoRef.current) return;
    
    try {
      setError('');
      setScanning(true);
      
      // Configurar hints para mejorar la precisión
      const hints = new Map();
      hints.set(2, true); // TRY_HARDER
      hints.set(3, true); // PURE_BARCODE
      codeReaderInstance.hints = hints;
      
      await codeReaderInstance.decodeFromVideoDevice(
        deviceId,
        videoRef.current,
        (result, err) => {
          if (result && !isProcessing) {
            const code = result.getText();
            
            // Evitar escaneos duplicados consecutivos
            if (code !== lastScanned) {
              setIsProcessing(true);
              setLastScanned(code);
              
              // Detener el escáner inmediatamente
              if (codeReaderInstance) {
                codeReaderInstance.reset();
              }
              
              // Vibrar si está disponible
              if (navigator.vibrate) {
                navigator.vibrate(200);
              }
              
              // Llamar al callback
              onScan(code);
              
              // Cerrar el escáner automáticamente
              handleClose();
            }
          }
          
          if (err && !(err instanceof NotFoundException)) {
            console.error('Scanning error:', err);
          }
        }
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('Error al acceder a la cámara: ' + err.message);
      setScanning(false);
    }
  };

  const stopScanning = () => {
    if (reader) {
      reader.reset();
      setReader(null);
    }
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    setScanning(false);
    setError('');
    setLastScanned('');
    setIsProcessing(false);
  };

  const handleClose = () => {
    stopScanning();
    onClose();
  };

  const handleDeviceChange = (e) => {
    const deviceId = e.target.value;
    setSelectedDeviceId(deviceId);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Escanear Código de Barras"
      size="large"
    >
      <div>
        {devices.length > 1 && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              Seleccionar cámara:
            </label>
            <CameraSelect value={selectedDeviceId} onChange={handleDeviceChange}>
              {devices.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Cámara ${device.deviceId}`}
                </option>
              ))}
            </CameraSelect>
          </div>
        )}
        
        <ScannerContainer>
          <VideoElement 
            ref={videoRef} 
            autoPlay 
            playsInline
            muted
          />
          {scanning && (
            <ScannerOverlay>
              <ScanArea>
                <ScanLine />
              </ScanArea>
            </ScannerOverlay>
          )}
        </ScannerContainer>

        {error && (
          <StatusMessage isError>
            {error}
          </StatusMessage>
        )}
        
        {lastScanned && (
          <StatusMessage>
            ✓ Escaneado: {lastScanned}
          </StatusMessage>
        )}
        
        <TipsList>
          <li>Mantenga el código dentro del recuadro</li>
          <li>Asegúrese de tener buena iluminación</li>
          <li>Evite reflejos en el código</li>
          <li>Mantenga la cámara estable</li>
        </TipsList>
        
        <Controls>
          <Button variant="outlined" onClick={handleClose}>
            Cerrar Escáner
          </Button>
        </Controls>
      </div>
    </Modal>
  );
};